import React, { useMemo } from "react";
import { FaArrowRight } from "react-icons/fa";

export default function TermometroConversao({
  empresaId,
  leads = [],
  recebLeads = [],
  ano,
  mes,
  considerarStatusFechado = true,
  debug = false,

  // filtros / extras
  captadorId,
  fonteId,
  procedimentoId,
  onOpenVendas,
  onOpenOportunidades,
  compact = false,

  // meta
  metaVendas = 150000,

  // NOVO
  mostrarMarcadores = true,
  baseMeta = "diasCorridos", // "diasCorridos" | "diasUteis"
  feriados = [],
}) {
  if (empresaId == null || empresaId === "") return null;
  const empresa = String(empresaId);

  /* ----------------- Helpers ----------------- */
  const norm = (s = "") =>
    String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const toNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return isFinite(v) ? v : 0;
    const s = String(v).trim();
    if (/^[\d.]+,\d{2}$/.test(s))
      return Number(s.replace(/\./g, "").replace(",", ".")) || 0;
    return Number(s.replace(/[^\d.\-]/g, "")) || 0;
  };

  const getMonthKey = (d) => {
    if (!d) return "";
    const s = String(d).trim();
    const mIso = s.match(/^(\d{4})-(\d{2})/);
    if (mIso) return `${mIso[1]}-${mIso[2]}`;
    const mYmdSlash = s.match(/^(\d{4})\/(\d{2})\//);
    if (mYmdSlash) return `${mYmdSlash[1]}-${mYmdSlash[2]}`;
    const mBr = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (mBr) return `${mBr[3]}-${mBr[2]}`;
    return "";
  };

  const prevMonth = (y, m) => {
    let yy = y,
      mm = m - 1;
    if (mm < 1) {
      mm = 12;
      yy -= 1;
    }
    return { y: yy, m: mm };
  };

  const fmt = (n) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(n || 0);

  const clampPct = (p) => Math.max(0, Math.min(100, p ?? 0));

  const fCaptador =
    captadorId == null || captadorId === "" ? null : String(captadorId);
  const fFonte = fonteId == null || fonteId === "" ? null : String(fonteId);
  const fProc =
    procedimentoId == null || procedimentoId === ""
      ? null
      : String(procedimentoId);

  const leadPassaFiltros = (lead) => {
    if (!lead) return false;
    if (String(lead?.empresa_id) !== empresa) return false;
    if (fCaptador && String(lead?.usuario_criador_id) !== fCaptador)
      return false;
    if (fFonte && String(lead?.id_fonte_lead) !== fFonte) return false;
    if (fProc && String(lead?.id_procedimento) !== fProc) return false;
    return true;
  };

  /* ----------------- Período atual e anterior ----------------- */
  const { y, m, alvoPrefix, alvoPrefixPrev, labelPeriodo, labelPeriodoPrev } =
    useMemo(() => {
      const now = new Date();
      const y = Number(ano ?? now.getFullYear());
      const m = Number(mes ?? now.getMonth() + 1);
      const { y: yPrev, m: mPrev } = prevMonth(y, m);
      return {
        y,
        m,
        alvoPrefix: `${y}-${String(m).padStart(2, "0")}`,
        alvoPrefixPrev: `${yPrev}-${String(mPrev).padStart(2, "0")}`,
        labelPeriodo: `${String(m).padStart(2, "0")}/${y}`,
        labelPeriodoPrev: `${String(mPrev).padStart(2, "0")}/${yPrev}`,
      };
    }, [ano, mes]);

  /* ----------------- Índice de leads ----------------- */
  const leadIndex = useMemo(() => {
    const map = new Map();
    for (const l of leads || []) {
      map.set(`${String(l?.empresa_id)}::${String(l?.id)}`, l);
    }
    return map;
  }, [leads]);

  /* ----------------- VENDAS: atual e anterior ----------------- */
  const { vendasAtual, vendasPrev } = useMemo(() => {
    let somaAtual = 0,
      somaPrev = 0;

    for (const r of recebLeads || []) {
      const lead = leadIndex.get(
        `${String(r?.empresa_id)}::${String(r?.lead_id)}`
      );
      if (!lead || !leadPassaFiltros(lead)) continue;

      if (considerarStatusFechado) {
        const st = norm(lead?.status_negociacao || "");
        if (st !== "fechado" && st !== "fechado parcial") continue;
      }

      const mk = getMonthKey(r?.data_recebimento);
      const val = toNumber(r?.valor);
      if (!val) continue;

      if (mk === alvoPrefix) somaAtual += val;
      else if (mk === alvoPrefixPrev) somaPrev += val;
    }

    if (debug) {
      console.debug(`[Termometro] VENDAS ${labelPeriodo}: ${somaAtual}`);
      console.debug(`[Termometro] VENDAS ${labelPeriodoPrev}: ${somaPrev}`);
    }
    return { vendasAtual: somaAtual, vendasPrev: somaPrev };
  }, [
    recebLeads,
    leadIndex,
    considerarStatusFechado,
    alvoPrefix,
    alvoPrefixPrev,
    labelPeriodo,
    labelPeriodoPrev,
    debug,
  ]);

  /* ----------------- OPORTUNIDADES: atual e anterior ----------------- */
  const { oppAtual, oppPrev } = useMemo(() => {
    let somaAtual = 0,
      somaPrev = 0;

    for (const l of leads || []) {
      if (!leadPassaFiltros(l)) continue;

      const mk = getMonthKey(l?.criado_em);
      const tot =
        toNumber(l?.total_orcamento) ||
        toNumber(l?.total_orcamento_lead) ||
        toNumber(l?.orcamento_total) ||
        0;
      if (!tot) continue;

      if (mk === alvoPrefix) somaAtual += tot;
      else if (mk === alvoPrefixPrev) somaPrev += tot;
    }

    if (debug) {
      console.debug(`[Termometro] OPP ${labelPeriodo}: ${somaAtual}`);
      console.debug(`[Termometro] OPP ${labelPeriodoPrev}: ${somaPrev}`);
    }
    return { oppAtual: somaAtual, oppPrev: somaPrev };
  }, [
    leads,
    alvoPrefix,
    alvoPrefixPrev,
    labelPeriodo,
    labelPeriodoPrev,
    debug,
  ]);

  /* ----------------- Métricas principais ----------------- */
  const taxaAtual =
    oppAtual > 0 ? Math.min(100, (vendasAtual / oppAtual) * 100) : 0;
  const taxaPrev =
    oppPrev > 0 ? Math.min(100, (vendasPrev / oppPrev) * 100) : 0;
  const deltaTaxa =
    taxaPrev === 0 && taxaAtual === 0
      ? 0
      : taxaPrev === 0
      ? 100
      : ((taxaAtual - taxaPrev) / taxaPrev) * 100;

  let cor = "#22c55e"; // verde
  if (taxaAtual < 30) cor = "#ef4444"; // vermelho
  else if (taxaAtual < 50) cor = "#f59e42"; // laranja

  const progressoMeta = clampPct((vendasAtual / metaVendas) * 100);
  const faltaMeta = Math.max(0, metaVendas - vendasAtual);

  /* ----------------- % Esperado até hoje (meta) ----------------- */
  const { esperadoAteHojePct, labelEsperado } = useMemo(() => {
    const first = new Date(y, m - 1, 1);
    const nextFirst = new Date(y, m, 1);
    const today = new Date();
    const ref = today < first ? first : today > nextFirst ? nextFirst : today;

    const isHoliday = (dt) => {
      const k = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(dt.getDate()).padStart(2, "0")}`;
      return feriados.includes(k);
    };

    if (baseMeta === "diasUteis") {
      let totalUteis = 0,
        uteisPassados = 0;
      for (let d = new Date(first); d < nextFirst; d.setDate(d.getDate() + 1)) {
        const dow = d.getDay();
        const util = dow >= 1 && dow <= 5 && !isHoliday(d);
        if (util) totalUteis++;
      }
      for (let d = new Date(first); d <= ref; d.setDate(d.getDate() + 1)) {
        const dow = d.getDay();
        const util = dow >= 1 && dow <= 5 && !isHoliday(d);
        if (util) uteisPassados++;
      }
      const frac = totalUteis > 0 ? uteisPassados / totalUteis : 0;
      return {
        esperadoAteHojePct: clampPct(frac * 100),
        labelEsperado: "esperado (dias úteis)",
      };
    } else {
      const totalDias = Math.round((nextFirst - first) / 86400000);
      const diasPassados = Math.round((ref - first) / 86400000) + 1;
      const frac = totalDias > 0 ? diasPassados / totalDias : 0;
      return {
        esperadoAteHojePct: clampPct(frac * 100),
        labelEsperado: "esperado (dias corridos)",
      };
    }
  }, [y, m, baseMeta, feriados]);

  /* ----------------- UI ----------------- */

  const MarkerDot = ({ pct, color, label, sublabel }) => {
    const p = clampPct(pct);
    return (
      <div
        className="absolute select-none"
        style={{
          left: `${p}%`,
          top: "100%",
          transform: "translate(-50%, -2px)", // encosta na barra
        }}
      >
        <div
          className="w-4 h-4 rounded-full border shadow-sm"
          style={{
            background: color,
            borderColor: "#ffffff",
            boxShadow: `0 0 0 3px ${color}33`,
          }}
          title={label}
        />
        <div
          className="mt-1 px-2 py-[2px] text-[10px] font-semibold rounded-md border bg-white/90 text-gray-800 whitespace-nowrap"
          style={{ borderColor: color }}
        >
          {label}
          {sublabel ? (
            <span className="ml-1 text-gray-500">• {sublabel}</span>
          ) : null}
        </div>
      </div>
    );
  };

  const Bar = () => (
    <div className="relative flex-1 mx-2 min-w-[60px] overflow-visible">
      <div className="h-3 bg-gray-200 rounded-full relative">
        <div
          className="h-3 rounded-full transition-all"
          style={{ width: `${taxaAtual}%`, background: cor }}
        />
        <div
          className="absolute top-0 left-0 h-3 rounded-full opacity-60 transition-all"
          style={{ width: `${progressoMeta}%`, background: "#3b82f6" }}
        />
        {mostrarMarcadores && (
          <>
            <MarkerDot
              pct={progressoMeta}
              color="#2563eb"
              label={`Hoje: ${progressoMeta.toFixed(1)}%`}
              sublabel={fmt(vendasAtual)}
            />
            <MarkerDot
              pct={esperadoAteHojePct}
              color="#f59e0b"
              label={`Esperado: ${esperadoAteHojePct.toFixed(1)}%`}
              sublabel={labelEsperado}
            />
          </>
        )}
      </div>
    </div>
  );

  const Delta = () => {
    const sign = deltaTaxa > 0 ? "+" : deltaTaxa < 0 ? "" : "";
    const arrow = deltaTaxa > 0 ? "↑" : deltaTaxa < 0 ? "↓" : "→";
    const corDelta =
      deltaTaxa > 0 ? "#16a34a" : deltaTaxa < 0 ? "#dc2626" : "#6b7280";
    return (
      <span
        className="ml-1 text-[11px] font-semibold"
        style={{ color: corDelta }}
      >
        {arrow} {sign}
        {isFinite(deltaTaxa) ? deltaTaxa.toFixed(1) : "0.0"}%
      </span>
    );
  };

  const LinhaValores = () => (
    <span className="flex items-center gap-1 ml-2 text-sm font-medium text-gray-800">
      <button
        type="button"
        onClick={onOpenVendas || undefined}
        className={`font-extrabold ${onOpenVendas ? "hover:underline" : ""}`}
      >
        {fmt(vendasAtual)}
      </button>
      <FaArrowRight className="text-gray-400 text-xs mx-1" />
      <button
        type="button"
        onClick={onOpenOportunidades || undefined}
        className={`font-bold text-gray-500 ${
          onOpenOportunidades ? "hover:underline" : ""
        }`}
      >
        {fmt(oppAtual)}
      </button>
    </span>
  );

  const BlocoMeta = () => (
    <span className="flex items-center gap-2 ml-3 text-[11px] text-gray-600">
      <span className="px-2 py-[2px] rounded-full border border-blue-200 bg-blue-50 font-semibold">
        Meta: {fmt(metaVendas)}
      </span>
      <span className="px-2 py-[2px] rounded-full border border-amber-200 bg-amber-50 font-semibold">
        Falta: {fmt(faltaMeta)}
      </span>
    </span>
  );

  return (
    <div className="flex items-center gap-2 w-full min-w-0 px-2 py-1 pb-8 relative">
      <span className="uppercase font-extrabold text-[12px] tracking-wide text-gray-700 flex-shrink-0">
        Conversão
      </span>

      <Bar />

      <span
        className="ml-1 text-base font-extrabold"
        style={{ color: cor, minWidth: 45, fontVariantNumeric: "tabular-nums" }}
      >
        {taxaAtual.toFixed(1)}%
      </span>

      {!compact && <Delta />}
      {!compact && <LinhaValores />}
      <BlocoMeta />
    </div>
  );
}
